import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Checkbox, message, Popconfirm, Space, Table, Tag, Tooltip, Typography } from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import roles from "roles";
import { FIRESTORE_CMS_ROLES_TABLE } from "constants/FirestoreConstant";
import { useCmsUsers } from "../../../../data/hooks/useCmsUsers";
import {
  ALL_PERMISSIONS,
  DEFAULT_ROLE_PERMISSIONS,
  PERMISSION_GROUPS,
  ROLE_ORDER,
  getCmsRole,
  getRolePermissions,
} from "../../../../functions/permissions";

const { Text } = Typography;

/** i18n-sleutel voor een permissie: "orders.view" → "orders_view" (punt is de keySeparator). */
export const permissionKey = (permission) => permission.replace(/\./g, "_");

const sameSet = (a, b) => _.isEqual([...a].sort(), [...b].sort());

/**
 * Beheer → Rollen: matrix rechten × rollen. Schrijft naar Firestore
 * `cmsRoles/{roleId}.permissions`; zonder document geldt de standaard uit
 * functions/permissions.js. Beheerder is vergrendeld (altijd alles).
 *
 * Wijzigingen gelden direct voor iedereen met die rol (usePermissions luistert
 * op cmsRoles). Per gebruiker afwijken kan in Beheer → Gebruikers.
 */
const Roles = () => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const auth = useSelector((state) => state.firebaseReducer.auth);
  const roleDocsList = useSelector((state) => state.firestoreReducer.ordered.cmsRoles);
  const cmsUsers = useCmsUsers();
  const [messageApi, contextHolder] = message.useMessage();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const roleDocs = useMemo(() => _.keyBy(roleDocsList || [], "id"), [roleDocsList]);
  const editableRoles = ROLE_ORDER.filter((role) => role !== roles.admin);

  const stored = useMemo(() => {
    const map = {};
    ROLE_ORDER.forEach((role) => {
      map[role] = getRolePermissions(role, roleDocs);
    });
    return map;
  }, [roleDocs]);

  // Concept volgt de opgeslagen stand zolang er niets gewijzigd is.
  useEffect(() => {
    if (!draft) setDraft(stored);
  }, [stored, draft]);

  const usersPerRole = useMemo(() => _.countBy(cmsUsers, (user) => getCmsRole(user)), [cmsUsers]);

  const dirtyRoles = useMemo(
    () => (draft ? editableRoles.filter((role) => !sameSet(draft[role] || [], stored[role] || [])) : []),
    [draft, stored, editableRoles],
  );

  const toggle = (role, permission, checked) => {
    setDraft((current) => {
      const next = new Set(current[role] || []);
      if (checked) next.add(permission);
      else next.delete(permission);
      return { ...current, [role]: ALL_PERMISSIONS.filter((p) => next.has(p)) };
    });
  };

  const resetToDefault = (role) => {
    setDraft((current) => ({ ...current, [role]: DEFAULT_ROLE_PERMISSIONS[role] }));
  };

  const discard = () => setDraft(stored);

  const save = async () => {
    setSaving(true);
    try {
      const batch = firestore.batch();
      dirtyRoles.forEach((role) => {
        batch.set(
          firestore.collection(FIRESTORE_CMS_ROLES_TABLE).doc(role),
          {
            permissions: draft[role],
            updatedAt: Math.floor(Date.now() / 1000),
            updatedBy: auth?.email || null,
          },
          { merge: true },
        );
      });
      await batch.commit();
      messageApi.success(t("roles.saved"));
    } catch (err) {
      console.error(err);
      messageApi.error(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const rows = PERMISSION_GROUPS.flatMap((group) => [
    { key: `group-${group.key}`, isGroup: true, label: t(`permission_groups.${group.key}`) },
    ...group.permissions.map((permission) => ({ key: permission, permission })),
  ]);

  const roleColumns = ROLE_ORDER.map((role) => {
    const locked = role === roles.admin;
    const isDefault = draft ? sameSet(draft[role] || [], DEFAULT_ROLE_PERMISSIONS[role]) : true;
    return {
      key: role,
      align: "center",
      width: 150,
      title: (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
          <Tooltip title={t(`roles.${role}.description`)}>
            <Text strong>{t(`roles.${role}.name`)}</Text>
          </Tooltip>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("roles.users_count", { count: usersPerRole[role] || 0 })}
          </Text>
          {locked ? (
            <Tag>{t("roles.admin_locked")}</Tag>
          ) : (
            <Space size={4}>
              <Tag color={isDefault ? "default" : "blue"}>{isDefault ? t("roles.default") : t("roles.customized")}</Tag>
              {!isDefault && (
                <Popconfirm
                  title={t("roles.reset_confirm", { role: t(`roles.${role}.name`) })}
                  onConfirm={() => resetToDefault(role)}
                  okText={t("yes")}
                  cancelText={t("no")}
                >
                  <Button type="text" size="small" icon={<ReloadOutlined />} title={t("roles.reset_to_default")} />
                </Popconfirm>
              )}
            </Space>
          )}
        </div>
      ),
      onCell: (row) => (row.isGroup ? { colSpan: 0 } : {}),
      render: (_value, row) =>
        row.isGroup ? null : (
          <Checkbox
            checked={locked || (draft?.[role] || []).includes(row.permission)}
            disabled={locked || !draft}
            onChange={(e) => toggle(role, row.permission, e.target.checked)}
          />
        ),
    };
  });

  const columns = [
    {
      key: "permission",
      title: t("roles.permission"),
      onCell: (row) => (row.isGroup ? { colSpan: 1 + ROLE_ORDER.length } : {}),
      render: (_value, row) => {
        if (row.isGroup) return <Text strong>{row.label}</Text>;
        const key = permissionKey(row.permission);
        const description = t(`permissions.${key}.description`);
        return (
          <div>
            <div>{t(`permissions.${key}.label`)}</div>
            {description && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {description}
              </Text>
            )}
          </div>
        );
      },
    },
    ...roleColumns,
  ];

  return (
    <>
      {contextHolder}
      <Card>
        <Alert type="info" showIcon message={t("roles.intro")} style={{ marginBottom: 16 }} />
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={rows}
            rowKey="key"
            pagination={false}
            size="small"
            rowClassName={(row) => (row.isGroup ? "ant-table-row-group" : "")}
            onRow={(row) => (row.isGroup ? { style: { background: "#fafafa" } } : {})}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 16 }}>
          {dirtyRoles.length > 0 && <Text type="warning">{t("roles.unsaved")}</Text>}
          <Button onClick={discard} disabled={dirtyRoles.length === 0 || saving}>
            {t("roles.discard")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={save}
            loading={saving}
            disabled={dirtyRoles.length === 0}
          >
            {t("roles.save")}
          </Button>
        </div>
      </Card>
    </>
  );
};

export default Roles;
